import React, { useEffect, useState, useCallback } from "react";
import moment from 'moment';
import { BASEURL, FILEURL } from "../Config";
import axios from 'axios';
import swal from 'sweetalert';
import ImageModal from "../components/ImageModal";
import ApplyLeaveModal from "../components/common/ApplyLeaveModal";
import { useHistory } from "react-router-dom";
import Select from 'react-select';

const TrackingSalesManager = (props) => {
    const { salesManagerUUID, salesManager, onStatsUpdate, initialTrackingState } = props;
    const history = useHistory();

    const [selectedDate, setSelectedDate] = useState(() => {
        if (initialTrackingState?.selectedDate) return new Date(initialTrackingState.selectedDate);
        return new Date();
    });
    const [currentMonth, setCurrentMonth] = useState(() => {
        if (initialTrackingState?.overviewYear && initialTrackingState?.overviewMonth) {
            return new Date(initialTrackingState.overviewYear, initialTrackingState.overviewMonth - 1, 1);
        }
        return new Date();
    });
    const [events, setEvents] = useState({});
    const [eventsOnSelectedDate, setEventsOnSelectedDate] = useState([]);
    const [attendanceData, setAttendanceData] = useState({});
    const [monthlyStats, setMonthlyStats] = useState({
        present: 0,
        absent: 0,
        leave: 0,
        events: 0,
        schools: 0,
        distributors: 0
    });

    const [showModal, setShowModal] = useState(false);
    const [isAddMode, setIsAddMode] = useState(true);
    const [schoolList, setSchoolList] = useState([]);
    const [distributorList, setDistributorList] = useState([]);
    const [purposeList, setPurposeList] = useState([]);

    const [eventDate, setEventDate] = useState('');
    const [visitingOption, setVisitingOption] = useState('');
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedDistributor, setSelectedDistributor] = useState('');
    const [selectedPurpose, setSelectedPurpose] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedEventUUID, setSelectedEventUUID] = useState('');
    const today = moment(new Date()).format('YYYY-MM-DD');

    const [showImageModal, setShowImageModal] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [leaveData, setLeaveData] = useState([]);
    const [leaveLoading, setLeaveLoading] = useState(false);

    const [overviewMonth, setOverviewMonth] = useState(initialTrackingState?.overviewMonth || new Date().getMonth() + 1);
    const [overviewYear, setOverviewYear] = useState(initialTrackingState?.overviewYear || new Date().getFullYear());

    const [showApplyLeaveModal, setShowApplyLeaveModal] = useState(false);
    const [showAllMonthEvents, setShowAllMonthEvents] = useState(false);
    const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(true);

    const currentYearNow = new Date().getFullYear();
    const currentMonthNow = new Date().getMonth() + 1;

    const handleOverviewYearChange = (newYear) => {
        setOverviewYear(newYear);
        if (newYear === currentYearNow && overviewMonth > currentMonthNow) {
            setOverviewMonth(currentMonthNow);
        }
    };

    const getAvailableMonths = () => {
        if (overviewYear === currentYearNow) {
            return moment.months().slice(0, currentMonthNow);
        }
        return moment.months();
    };

    const formatDate = useCallback((date) => {
        return moment(date).format('YYYY-MM-DD');
    }, []);

    const fetchAttendance = useCallback(async (month, year) => {
        try {
            const response = await axios.get(
                `${BASEURL}/api/v1/salesManagerAttendance/getAttendance?salesManagerUUID=${salesManagerUUID}&month=${month}&year=${year}`
            );
            if (response.data && Object.keys(response.data).length > 0) {
                const formattedData = {};
                let presentCount = 0;
                let absentCount = 0;
                let leaveCount = 0;

                Object.keys(response.data).forEach(date => {
                    const formattedDate = moment(date).format('YYYY-MM-DD');
                    const status = response.data[date][0]?.status?.toLowerCase();
                    formattedData[formattedDate] = status;

                    if (status === 'present') presentCount++;
                    else if (status === 'absent') absentCount++;
                    else if (status === 'leave' || status === 'holiday') leaveCount++;
                });

                setAttendanceData(formattedData);
                setMonthlyStats(prev => ({
                    ...prev,
                    present: presentCount,
                    absent: absentCount,
                    leave: leaveCount
                }));
            } else {
                setAttendanceData({});
                setMonthlyStats(prev => ({
                    ...prev,
                    present: 0,
                    absent: 0,
                    leave: 0
                }));
            }
        } catch (err) {
            console.error("Fetch attendance error", err);
        }
    }, [salesManagerUUID]);

    const fetchEvents = useCallback(async (date) => {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        try {
            const response = await axios.get(
                `${BASEURL}/api/v1/calendarEvent/eventsData?salesManagerUUID=${salesManagerUUID}&month=${month}&year=${year}`
            );
            setEvents(response.data);

            const eventCount = Object.keys(response.data).reduce((acc, key) => {
                return acc + (response.data[key]?.length || 0);
            }, 0);

            setMonthlyStats(prev => ({
                ...prev,
                events: eventCount
            }));
        } catch (err) {
            console.error("Fetch eventsByMonthYear error", err);
        }
    }, [salesManagerUUID]);

    const fetchEventsForSelectedDate = useCallback(async (date) => {
        const formattedDate = formatDate(date);
        try {
            const response = await axios.get(
                `${BASEURL}/api/v1/calendarEvent/byDate?salesManagerUUID=${salesManagerUUID}&date=${formattedDate}`
            );
            setEventsOnSelectedDate(response.data[formattedDate] || []);
        } catch (err) {
            console.error("Fetch eventsByDate error", err);
        }
    }, [salesManagerUUID, formatDate]);

    const fetchSchoolList = useCallback(async () => {
        try {
            if (!salesManager || !salesManager.states || !salesManager.cities) {
                setSchoolList([]);
                return;
            }

            const stateNames = salesManager.states.map(s => s.label).join(',');
            const cityNames = salesManager.cities.map(c => c.label).join(',');
            
            let url = `${BASEURL}/api/v1/salesSchool/fetchByQuery?`;
            if (stateNames) url += `states=${stateNames}&`;
            if (cityNames) url += `cities=${cityNames}&`;
            
            const response = await axios.get(url);
            const schools = response.data?.schools || [];
            setSchoolList(Array.isArray(schools) ? schools : []);
            const count = response.data?.total || schools.length || 0;
            setMonthlyStats(prev => ({
                ...prev,
                schools: count
            }));
        } catch (error) {
            console.error('Error fetching schools:', error);
            setSchoolList([]);
        }
    }, [salesManager, overviewMonth, overviewYear, onStatsUpdate]);

    const fetchDistributorList = useCallback(async () => {
        try {
            if (!salesManager || !salesManager.states || !salesManager.cities) {
                setDistributorList([]);
                return;
            }

            const stateNames = salesManager.states.map(s => s.label).join(',');
            const cityNames = salesManager.cities.map(c => c.label).join(',');
            
            let url = `${BASEURL}/api/v1/distributor/fetchByQuery?`;
            if (stateNames) url += `states=${stateNames}&`;
            if (cityNames) url += `cities=${cityNames}&`;
            
            const response = await axios.get(url);
            const distributors = response.data?.distributors || [];
            setDistributorList(Array.isArray(distributors) ? distributors : []);
            const count = response.data?.total || distributors.length || 0;
            setMonthlyStats(prev => ({
                ...prev,
                distributors: count
            }));
        } catch (error) {
            console.error('Error fetching distributors:', error);
            setDistributorList([]);
        }
    }, [salesManager, overviewMonth, overviewYear, onStatsUpdate]);

    const fetchPurposeList = useCallback(async () => {
        try {
            const response = await axios.get(`${BASEURL}/api/v1/purposeOfVisit`);
            setPurposeList(response.data);
        } catch (error) {
            console.error('Error fetching purposes List:', error);
        }
    }, []);

    const fetchOverviewStats = useCallback(async (month, year) => {
        setMonthlyStatsLoading(true);
        try {
            // Fetch attendance stats for overview
            const attendanceRes = await axios.get(
                `${BASEURL}/api/v1/salesManagerAttendance/getAttendance?salesManagerUUID=${salesManagerUUID}&month=${month}&year=${year}`
            );
            let presentCount = 0, absentCount = 0, leaveCount = 0;
            if (attendanceRes.data && Object.keys(attendanceRes.data).length > 0) {
                Object.keys(attendanceRes.data).forEach(date => {
                    const status = attendanceRes.data[date][0]?.status?.toLowerCase();
                    if (status === 'present') presentCount++;
                    else if (status === 'absent') absentCount++;
                    else if (status === 'leave' || status === 'holiday') leaveCount++;
                });
            }

            // Fetch events count for overview
            const overviewDate = new Date(year, month - 1, 1);
            const eventsRes = await axios.get(
                `${BASEURL}/api/v1/calendarEvent/eventsData?salesManagerUUID=${salesManagerUUID}&month=${month}&year=${year}`
            );
            const eventCount = Object.keys(eventsRes.data).reduce((acc, key) => {
                return acc + (eventsRes.data[key]?.length || 0);
            }, 0);

            setMonthlyStats(prev => ({
                ...prev,
                present: presentCount,
                absent: absentCount,
                leave: leaveCount,
                events: eventCount
            }));
        } catch (err) {
            console.error("Fetch overview stats error", err);
        } finally {
            setMonthlyStatsLoading(false);
        }
    }, [salesManagerUUID]);

    useEffect(() => {
        fetchOverviewStats(overviewMonth, overviewYear);
        fetchSchoolList();
        fetchDistributorList();
    }, [overviewMonth, overviewYear, fetchOverviewStats, fetchSchoolList, fetchDistributorList]);

    useEffect(() => {
        const month = currentMonth.getMonth() + 1;
        const year = currentMonth.getFullYear();

        fetchEvents(currentMonth);
        fetchAttendance(month, year);
        fetchSchoolList();
        fetchDistributorList();
        fetchPurposeList();
        fetchEventsForSelectedDate(selectedDate);
    }, [currentMonth, fetchEvents, fetchAttendance, fetchSchoolList, fetchDistributorList, fetchPurposeList, fetchEventsForSelectedDate, selectedDate]);

    const onSelectDate = (date) => {
        setSelectedDate(date);
        setShowAllMonthEvents(false);
        fetchEventsForSelectedDate(date);
    };

    const navigateMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
        setOverviewMonth(newMonth.getMonth() + 1);
        setOverviewYear(newMonth.getFullYear());
        setEventsOnSelectedDate([]);
        setShowAllMonthEvents(false);
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month days
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthDays - i),
                isCurrentMonth: false
            });
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: new Date(year, month, i),
                isCurrentMonth: true
            });
        }

        // Next month days
        const remainingDays = 42 - days.length;
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                isCurrentMonth: false
            });
        }

        return days;
    };

    const getDateStatus = (date) => {
        const dateKey = formatDate(date);
        return attendanceData[dateKey] || null;
    };

    const isToday = (date) => {
        const todayDate = new Date();
        return date.getDate() === todayDate.getDate() &&
            date.getMonth() === todayDate.getMonth() &&
            date.getFullYear() === todayDate.getFullYear();
    };

    const isSelected = (date) => {
        return date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear();
    };

    const handleEventChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case 'eventDate':
                setEventDate(value);
                break;
            case 'visitingOption':
                setVisitingOption(value);
                setSelectedSchool('');
                setSelectedDistributor('');
                break;
            case 'school':
                setSelectedSchool(value);
                break;
            case 'distributor':
                setSelectedDistributor(value);
                break;
            case 'purpose':
                setSelectedPurpose(value);
                break;
            case 'notes':
                setNotes(value);
                break;
            default:
                break;
        }
    };

    const openAddModal = () => {
        setIsAddMode(true);
        resetEventModal();
        setEventDate(formatDate(selectedDate));
        setShowModal(true);
    };

    const openUpdateModal = (event) => {
        const eventDate = new Date(event.date);
        if (event.status === null && eventDate >= new Date().setHours(0, 0, 0, 0)) {
            setIsAddMode(false);
            setEventDate(formatDate(event.date));
            setVisitingOption(event.visiting);
            setSelectedSchool(event.visiting === "School" ? event.visitorUUID : '');
            setSelectedDistributor(event.visiting === "Distributor" ? event.visitorUUID : '');
            setSelectedPurpose(event.purpose);
            setNotes(event.notes);
            setSelectedEventUUID(event.id);
            setShowModal(true);
        }
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const resetEventModal = () => {
        setEventDate('');
        setVisitingOption('');
        setSelectedSchool('');
        setSelectedDistributor('');
        setSelectedPurpose('');
        setNotes('');
        setSelectedEventUUID('');
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        // Validate school/distributor selection
        if (visitingOption === 'School' && !selectedSchool) {
            swal('Error', 'Please select a school', 'error');
            return;
        }
        if (visitingOption === 'Distributor' && !selectedDistributor) {
            swal('Error', 'Please select a distributor', 'error');
            return;
        }

        const data = {
            salesManagerUUID: salesManagerUUID,
            date: eventDate,
            visiting: visitingOption,
            visitorUUID: visitingOption === 'School' ? selectedSchool : selectedDistributor,
            purposeOfVisit: selectedPurpose,
            notes: notes
        };

        const request = isAddMode
            ? axios.post(`${BASEURL}/api/v1/calendarEvent`, data)
            : axios.put(`${BASEURL}/api/v1/calendarEvent/${selectedEventUUID}`, data);

        request.then(res => {
            swal("Success", `Event ${isAddMode ? 'Added' : 'Updated'} successfully`, "success");
            fetchEvents(new Date(eventDate));
            setShowModal(false);
            resetEventModal();
            fetchEventsForSelectedDate(eventDate);
            if (onStatsUpdate) {
                onStatsUpdate(prev => ({ ...prev, eventsCount: (prev.eventsCount || 0) + (isAddMode ? 1 : 0) }));
            }
        }).catch((err) => {
            swal('Error', `Failed to ${isAddMode ? 'add' : 'update'} event`, 'error');
            console.error('Event Error', err);
        });
    };

    const handleDeleteEvent = (eventId) => {
        swal({
            title: "Are you sure?",
            text: "This event will be deleted permanently.",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                axios.delete(`${BASEURL}/api/v1/calendarEvent/${eventId}`)
                    .then(() => {
                        swal("Success", "Event deleted successfully", "success");
                        fetchEvents(currentMonth);
                        fetchEventsForSelectedDate(selectedDate);
                    })
                    .catch((err) => {
                        swal("Error", "Failed to delete event", "error");
                        console.error("Delete error", err);
                    });
            }
        });
    };

    const navigateToVisitor = (event) => {
        if (event.visiting === 'school') {
            history.push(`/viewSchoolData/${event.visitorUUID}`);
        } else if (event.visiting === 'distributor') {
            history.push(`/viewDistributor/${event.visitorUUID}`);
        }
    };

    const openImageModal = (fileData) => {
        const url = FILEURL + '/' + fileData.key;
        setImageUrl(url);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setImageUrl('');
    };

    const fetchLeaveData = async () => {
        setLeaveLoading(true);
        try {
            const response = await axios.get(
                `${BASEURL}/api/v1/salesManagerAttendance/getLeaves?salesManagerUUID=${salesManagerUUID}&month=${overviewMonth}&year=${overviewYear}`
            );
            setLeaveData(response.data || []);
        } catch (err) {
            console.error("Fetch leave data error", err);
            setLeaveData([]);
        }
        setLeaveLoading(false);
    };

    const openLeaveModal = () => {
        fetchLeaveData();
        setShowLeaveModal(true);
    };

    const handleLeaveAction = async (leaveId, action) => {
        try {
            await axios.put(`${BASEURL}/api/v1/salesManagerAttendance/leaveAction/${leaveId}`, { action });
            swal("Success", `Leave ${action}ed successfully`, "success");
            fetchLeaveData();
            fetchOverviewStats(overviewMonth, overviewYear);
        } catch (err) {
            console.error("Leave action error", err);
            swal("Error", `Failed to ${action} leave`, "error");
        }
    };

    const handleApplyLeaveSuccess = () => {
        fetchOverviewStats(overviewMonth, overviewYear);
    };

    const getAttendanceStatus = () => {
        const dateKey = formatDate(selectedDate);
        return attendanceData[dateKey] || null;
    };

    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const days = getDaysInMonth(currentMonth);

    return (
        <div className="tracking-section">
            {/* Monthly Overview - Above Tracking */}
            <div className="monthly-overview-section">
                <div className="overview-header">
                    <h5 className="overview-title">Monthly Overview</h5>
                    <div className="overview-header-controls">
                        <select
                            className="overview-select"
                            value={overviewMonth}
                            onChange={(e) => setOverviewMonth(parseInt(e.target.value))}
                        >
                            {getAvailableMonths().map((m, idx) => (
                                <option key={idx} value={idx + 1}>{m}</option>
                            ))}
                        </select>
                        <select
                            className="overview-select"
                            value={overviewYear}
                            onChange={(e) => handleOverviewYearChange(parseInt(e.target.value))}
                        >
                            {Array.from({ length: 3 }, (_, i) => currentYearNow - 2 + i).map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="overview-grid overview-grid-padded">
                    {monthlyStatsLoading ? (
                        // Loading skeleton
                        Array.from({ length: 6 }).map((_, idx) => (
                            <div key={idx} className="overview-card loading-card">
                                <div className="overview-icon loading-skeleton">
                                    <div className="skeleton-circle"></div>
                                </div>
                                <div className="overview-stat">
                                    <div className="skeleton-line skeleton-h3"></div>
                                    <div className="skeleton-line skeleton-p"></div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <>
                    <div className="overview-card present-card">
                        <div className="overview-icon present">
                            <i className="mdi mdi-calendar-check" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.present}</h3>
                            <p>Present</p>
                        </div>
                    </div>
                    <div className="overview-card absent-card">
                        <div className="overview-icon absent">
                            <i className="mdi mdi-calendar-remove" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.absent}</h3>
                            <p>Absent</p>
                        </div>
                    </div>
                    <div className="overview-card leave-card" onClick={openLeaveModal}>
                        <div className="overview-icon leave">
                            <i className="mdi mdi-calendar-clock" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.leave}</h3>
                            <p>Leave or Holiday</p>
                        </div>
                    </div>
                    <div className="overview-card events-card" onClick={() => {
                        const firstDayOfMonth = new Date(overviewYear, overviewMonth - 1, 1);
                        setCurrentMonth(firstDayOfMonth);
                        setSelectedDate(firstDayOfMonth);
                        setShowAllMonthEvents(true);
                    }}>
                        <div className="overview-icon events">
                            <i className="mdi mdi-map-marker" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.events}</h3>
                            <p>Events</p>
                        </div>
                    </div>
                    <div className="overview-card schools-card" onClick={() => {
                        const startDate = moment().year(overviewYear).month(overviewMonth - 1).startOf('month').format('YYYY-MM-DD');
                        const endDate = moment().year(overviewYear).month(overviewMonth - 1).endOf('month').format('YYYY-MM-DD');
                        history.push(`/schoolsData?createdBy=${salesManagerUUID}&startDate=${startDate}&endDate=${endDate}&overviewMonth=${overviewMonth}&overviewYear=${overviewYear}&selectedDate=${formatDate(selectedDate)}`);
                    }}>
                        <div className="overview-icon schools">
                            <i className="mdi mdi-school" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.schools}</h3>
                            <p>Schools Added</p>
                        </div>
                    </div>
                    <div className="overview-card distributors-card" onClick={() => {
                        const startDate = moment().year(overviewYear).month(overviewMonth - 1).startOf('month').format('YYYY-MM-DD');
                        const endDate = moment().year(overviewYear).month(overviewMonth - 1).endOf('month').format('YYYY-MM-DD');
                        history.push(`/distributors?createdBy=${salesManagerUUID}&startDate=${startDate}&endDate=${endDate}&overviewMonth=${overviewMonth}&overviewYear=${overviewYear}&selectedDate=${formatDate(selectedDate)}`);
                    }}>
                        <div className="overview-icon distributors">
                            <i className="mdi mdi-account-group" />
                        </div>
                        <div className="overview-stat">
                            <h3>{monthlyStats.distributors}</h3>
                            <p>Distributors Added</p>
                        </div>
                    </div>
                        </>
                    )}
                </div>
            </div>

            <div className="tracking-header">
                <h4>
                    <i className="mdi mdi-map-marker" />
                    Tracking
                </h4>
                <div className="tracking-header-buttons">
                    <button className="add-event-btn leave-btn" onClick={() => setShowApplyLeaveModal(true)}>
                        <i className="mdi mdi-calendar-clock" /> Apply Leave / Holiday
                    </button>
                    <button className="add-event-btn" onClick={openAddModal}>
                        <i className="mdi mdi-plus" /> Add Event
                    </button>
                </div>
            </div>

            <div className="tracking-body">
                {/* Calendar Section */}
                <div className="calendar-container">
                    <div className="calendar-nav">
                        <button onClick={() => navigateMonth(-1)}>
                            <i className="mdi mdi-chevron-left" />
                        </button>
                        <h5>{moment(currentMonth).format('MMMM YYYY')}</h5>
                        <button onClick={() => navigateMonth(1)}>
                            <i className="mdi mdi-chevron-right" />
                        </button>
                    </div>

                    <div className="calendar-grid">
                        {weekDays.map((day, idx) => (
                            <div key={idx} className="calendar-header-cell">{day}</div>
                        ))}
                        {days.map((day, idx) => {
                            const status = getDateStatus(day.date);
                            const hasEvents = events[formatDate(day.date)]?.length > 0;

                            return (
                                <div
                                    key={idx}
                                    className={`calendar-cell
                                        ${!day.isCurrentMonth ? 'other-month' : ''}
                                        ${isSelected(day.date) ? 'selected' : ''}
                                        ${isToday(day.date) ? 'today' : ''}
                                        ${status || ''}`}
                                    onClick={() => onSelectDate(day.date)}
                                >
                                    {day.date.getDate()}
                                    {hasEvents && !isSelected(day.date) && (
                                        <span className="calendar-event-dot" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="calendar-legend">
                        <div className="legend-item">
                            <div className="legend-dot present"></div>
                            <span>Present</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-dot absent"></div>
                            <span>Absent</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-dot leave"></div>
                            <span>Leave / Holiday</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-dot today"></div>
                            <span>Today</span>
                        </div>
                    </div>
                </div>

                {/* Events Panel */}
                <div className="overview-container">
                    <div className="events-panel">
                        <div className="events-panel-header">
                            <i className="mdi mdi-calendar" />
                            <h5>
                                {showAllMonthEvents 
                                    ? `${Object.values(events).flat().length} Event(s) in ${moment(currentMonth).format('MMMM YYYY')}`
                                    : `${eventsOnSelectedDate.length} Event(s) on ${moment(selectedDate).format('MMMM D, YYYY')}`
                                }
                            </h5>
                            {!showAllMonthEvents && getAttendanceStatus() && (
                                <span className={`badge ${getAttendanceStatus()}`}>
                                    {getAttendanceStatus()?.charAt(0).toUpperCase() + getAttendanceStatus()?.slice(1)}
                                </span>
                            )}
                        </div>

                        <div className="events-panel-scroll">
                        {showAllMonthEvents ? (
                            Object.values(events).flat().length > 0 ? (
                                Object.keys(events).sort().map(dateKey => (
                                    events[dateKey]?.length > 0 && (
                                        <div key={dateKey}>
                                            <div className="event-date-group-header">
                                                {moment(dateKey).format('MMMM D, YYYY (dddd)')}
                                            </div>
                                            {events[dateKey].map((event, idx) => (
                                                <div key={idx} className="event-card">
                                                    <div className="event-card-header">
                                                        <span
                                                            className="event-school-name"
                                                            onClick={() => navigateToVisitor(event)}
                                                        >
                                                            {event.visitor}
                                                        </span>
                                                        <div className="event-actions">
                                                            {event.file && (
                                                                <button className="attachment" onClick={() => openImageModal(event.file)}>
                                                                    <i className="mdi mdi-file-image-outline" />
                                                                </button>
                                                            )}
                                                            {event.status === null && new Date(event.date) >= new Date().setHours(0, 0, 0, 0) && (
                                                                <button className="edit" onClick={() => openUpdateModal(event)}>
                                                                    <i className="mdi mdi-pencil-outline" />
                                                                </button>
                                                            )}
                                                            {new Date(event.date) >= new Date().setHours(0, 0, 0, 0) && (
                                                                <button className="delete" onClick={() => handleDeleteEvent(event.id)}>
                                                                    <i className="mdi mdi-trash-can-outline" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="category-badge">{event.visiting}</div>
                                                    <div className="event-detail">
                                                        <strong>Purpose:</strong> {event.purpose}
                                                    </div>
                                                    {event.notes && (
                                                        <div className="event-detail">
                                                            <strong>Notes:</strong> {event.notes}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ))
                            ) : (
                                <div className="empty-state">
                                    <i className="mdi mdi-calendar-blank" />
                                    <p>No Events Added</p>
                                </div>
                            )
                        ) : (
                            eventsOnSelectedDate.length > 0 ? (
                                eventsOnSelectedDate.map((event, idx) => (
                                    <div key={idx} className="event-card">
                                        <div className="event-card-header">
                                            <span
                                                className="event-school-name"
                                                onClick={() => navigateToVisitor(event)}
                                            >
                                                {event.visitor}
                                            </span>
                                            <div className="event-actions">
                                                {event.file && (
                                                    <button onClick={() => openImageModal(event.file)}>
                                                        <i className="mdi mdi-attachment" />
                                                    </button>
                                                )}
                                                {event.status === null && new Date(event.date) >= new Date().setHours(0, 0, 0, 0) && (
                                                    <button onClick={() => openUpdateModal(event)}>
                                                        <i className="mdi mdi-pencil" />
                                                    </button>
                                                )}
                                                {new Date(event.date) >= new Date().setHours(0, 0, 0, 0) && (
                                                    <button className="delete" onClick={() => handleDeleteEvent(event.id)}>
                                                        <i className="mdi mdi-trash-can" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="category-badge">{event.visiting}</div>
                                        <div className="event-detail">
                                            <strong>Purpose:</strong> {event.purpose}
                                        </div>
                                        {event.notes && (
                                            <div className="event-detail">
                                                <strong>Notes:</strong> {event.notes}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <i className="mdi mdi-calendar-blank" />
                                    <p>No Events Added</p>
                                </div>
                            )
                        )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Event Modal */}
            {showModal && (
                <div className="enhanced-modal-overlay" onClick={closeModal}>
                    <div className="enhanced-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="enhanced-modal-header">
                            <h5>{isAddMode ? 'Add Event' : 'Update Event'}</h5>
                            <button onClick={closeModal}>&times;</button>
                        </div>
                        <form onSubmit={handleFormSubmit}>
                            <div className="enhanced-modal-body">
                                <div className="form-group">
                                    <label>Event Date</label>
                                    <input
                                        type="date"
                                        name="eventDate"
                                        value={eventDate}
                                        onChange={handleEventChange}
                                        min={today}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Visiting Option</label>
                                    <select
                                        name="visitingOption"
                                        value={visitingOption}
                                        onChange={handleEventChange}
                                        required
                                    >
                                        <option value="">Select</option>
                                        <option value="School">School</option>
                                        <option value="Distributor">Distributor</option>
                                    </select>
                                </div>
                                {visitingOption === 'School' && (
                                    <div className="form-group">
                                        <label>Select School</label>
                                        <Select
                                            name="school"
                                            value={Array.isArray(schoolList) && selectedSchool ? schoolList.filter(s => s.uuid === selectedSchool).map(s => ({ value: s.uuid, label: s.schoolName }))[0] || null : null}
                                            onChange={(option) => setSelectedSchool(option ? option.value : '')}
                                            options={Array.isArray(schoolList) ? schoolList.map(school => ({ value: school.uuid, label: school.schoolName })) : []}
                                            placeholder="Search and select school..."
                                            isClearable
                                            isSearchable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    borderRadius: '8px',
                                                    borderColor: '#ddd',
                                                    minHeight: '42px',
                                                    '&:hover': { borderColor: '#667eea' }
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    zIndex: 9999
                                                })
                                            }}
                                        />
                                        <input
                                            type="hidden"
                                            value={selectedSchool}
                                            required
                                        />
                                    </div>
                                )}
                                {visitingOption === 'Distributor' && (
                                    <div className="form-group">
                                        <label>Select Distributor</label>
                                        <Select
                                            name="distributor"
                                            value={Array.isArray(distributorList) && selectedDistributor ? distributorList.filter(d => d.uuid === selectedDistributor).map(d => ({ value: d.uuid, label: d.name }))[0] || null : null}
                                            onChange={(option) => setSelectedDistributor(option ? option.value : '')}
                                            options={Array.isArray(distributorList) ? distributorList.map(distributor => ({ value: distributor.uuid, label: distributor.name })) : []}
                                            placeholder="Search and select distributor..."
                                            isClearable
                                            isSearchable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    borderRadius: '8px',
                                                    borderColor: '#ddd',
                                                    minHeight: '42px',
                                                    '&:hover': { borderColor: '#667eea' }
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    zIndex: 9999
                                                })
                                            }}
                                        />
                                        <input
                                            type="hidden"
                                            value={selectedDistributor}
                                            required
                                        />
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Purpose</label>
                                    <select
                                        name="purpose"
                                        value={selectedPurpose}
                                        onChange={handleEventChange}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {purposeList.map(purpose => (
                                            <option key={purpose._id || purpose.label} value={purpose.label}>{purpose.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Notes</label>
                                    <textarea
                                        name="notes"
                                        value={notes}
                                        onChange={handleEventChange}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <div className="enhanced-modal-footer">
                                <button type="button" className="btn-cancel" onClick={closeModal}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-save">
                                    {isAddMode ? 'Add' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showImageModal && <ImageModal imageUrl={imageUrl} closeModal={closeImageModal} />}

            {/* Leave Details Modal */}
            {showLeaveModal && (
                <div className="enhanced-modal-overlay" onClick={() => setShowLeaveModal(false)}>
                    <div className="enhanced-modal modal-md" onClick={(e) => e.stopPropagation()}>
                        <div className="enhanced-modal-header">
                            <h5>Leave Details - {moment().month(overviewMonth - 1).format('MMMM')} {overviewYear}</h5>
                            <button onClick={() => setShowLeaveModal(false)}>&times;</button>
                        </div>
                        <div className="enhanced-modal-body scrollable">
                            {leaveLoading ? (
                                <div className="empty-state">
                                    <p>Loading...</p>
                                </div>
                            ) : leaveData.length > 0 ? (
                                leaveData.map((leave, idx) => (
                                    <div key={idx} className="event-card leave-card-item">
                                        <div className="leave-card-layout">
                                            <div>
                                                <div className="leave-card-date">
                                                    {moment(leave.date).format('MMMM D, YYYY (dddd)')}
                                                </div>
                                                <div className="leave-card-reason">
                                                    <strong>Reason:</strong> {leave.reason || 'No reason provided'}
                                                </div>
                                                {leave.status && (
                                                    <span className={`leave-status-badge ${leave.status}`}>
                                                        {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                                                    </span>
                                                )}
                                            </div>
                                            {(!leave.status || leave.status === 'pending') && (
                                                <div className="leave-action-buttons">
                                                    <button className="btn-approve" onClick={() => handleLeaveAction(leave.id, 'approve')}>
                                                        Approve
                                                    </button>
                                                    <button className="btn-reject" onClick={() => handleLeaveAction(leave.id, 'reject')}>
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <i className="mdi mdi-calendar-blank" />
                                    <p>No leaves found for this month</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Apply Leave / Holiday Modal */}
            <ApplyLeaveModal
                show={showApplyLeaveModal}
                onClose={() => setShowApplyLeaveModal(false)}
                onSuccess={handleApplyLeaveSuccess}
                salesManagerUUID={salesManagerUUID}
                salesManagerName={salesManager ? `${salesManager.firstName} ${salesManager.lastName}` : ''}
            />
        </div>
    );
};

export default TrackingSalesManager;
